import { Box, Heading, Paragraph } from 'grommet';
import { useParams } from 'react-router';

import { useCharityEventDetails } from 'api/charityEventAPI';
import { EventDescription } from 'components/shared/EventDescription';

/**
 * Where Stripe sends someone after they pay.
 *
 * It arrives as a full page load — this is Stripe's `return_url`, not a
 * client-side navigation — so nothing the registration form knew survives, and
 * the event has to be fetched again. The URL is all we start with.
 *
 * What must never depend on that fetch is the sentence telling someone their
 * payment worked. It is true the moment they arrive, it is the only thing they
 * came here to read, and interpolating a not-yet-loaded title into it produced
 * a heading that read "You are registered for the undefined!" for as long as the
 * request took. `undefined` in a template literal is the string "undefined", so
 * the optional chaining that looks like a guard is not one.
 *
 * So the certain message renders immediately and the event's details fill in
 * behind it. That also degrades correctly: `useCharityEventDetails` reports a
 * failure through the error handler and leaves the event undefined forever, in
 * which case this page keeps saying the true and important thing rather than
 * settling on a hole.
 */
export const EventRegistrationConfirmation = () => {
    const { eventId, email } = useParams();
    const event = useCharityEventDetails(eventId);
    return (
        <Box alignSelf="center" width="xlarge" pad={{ bottom: 'medium' }}>
            <Heading level={2}>
                {event ? `You are registered for the ${event.eventTitle}!` : 'You are registered!'}
            </Heading>
            {/* Rendered only once there is something to put in it. Every field
                inside is individually optional-chained, so an absent event
                yields a block of empty rows rather than nothing at all. */}
            {event && <EventDescription event={event} />}
            <Paragraph>Primary Contact: {email}</Paragraph>
            <Heading level={3}>Thank you for your support!</Heading>
        </Box>
    );
};
